export type StructuredDirectiveType = "earnings" | "transcript";

export interface StructuredDirective {
    type: StructuredDirectiveType;
    params: Record<string, string>;
    raw: string;
}

interface ParsedDirective {
    cleanedText: string;
    directives: StructuredDirective[];
}

const DIRECTIVE_HEADING_REGEX = /^##\s*(earnings|transcript)[^\n]*(?:\r?\n(?!##\s).*)*/gim;

const KEY_VALUE_REGEX = /([A-Za-z0-9_]+)\s*[:=]\s*("[^"]*"|'[^']*'|`[^`]*`|[^\s,]+)/g;

const stripQuotes = (value: string) => {
    const trimmed = value.trim();
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'")) || (trimmed.startsWith('`') && trimmed.endsWith('`'))) {
        return trimmed.slice(1, -1);
    }
    return trimmed;
};

const parseKeyValuePairs = (input: string): Record<string, string> => {
    const params: Record<string, string> = {};
    if (!input) return params;
    const trimmed = input.trim();
    if (!trimmed) return params;

    // Try JSON first
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
            const parsed = JSON.parse(trimmed);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                for (const [key, value] of Object.entries(parsed)) {
                    if (value === null || value === undefined) continue;
                    params[key] = String(value);
                }
                if (Object.keys(params).length > 0) {
                    return params;
                }
            }
        } catch (err) {
            // fall through to other strategies
        }
    }

    // Inline key=value pairs
    let match: RegExpExecArray | null;
    while ((match = KEY_VALUE_REGEX.exec(trimmed)) !== null) {
        const key = match[1];
        const value = stripQuotes(match[2]);
        if (key) {
            params[key] = value;
        }
    }

    if (Object.keys(params).length > 0) {
        return params;
    }

    // Line-based parsing ("key: value")
    const lines = trimmed
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    for (const line of lines) {
        const cleanedLine = line.replace(/^[-*]\s*/, "");
        const separatorIndex = cleanedLine.indexOf(":");
        const equalsIndex = cleanedLine.indexOf("=");
        let idx = -1;
        if (separatorIndex !== -1 && equalsIndex !== -1) {
            idx = Math.min(separatorIndex, equalsIndex);
        } else {
            idx = Math.max(separatorIndex, equalsIndex);
        }

        if (idx !== -1) {
            const key = cleanedLine.slice(0, idx).trim();
            const value = cleanedLine.slice(idx + 1).trim();
            if (key) {
                params[key] = stripQuotes(value);
            }
        }
    }

    return params;
};

export const parseStructuredMarkdown = (input: string | undefined | null): ParsedDirective => {
    if (!input) {
        return { cleanedText: "", directives: [] };
    }

    const directives: StructuredDirective[] = [];
    let cleanedText = input;

    cleanedText = cleanedText.replace(DIRECTIVE_HEADING_REGEX, (match: string, rawType: string) => {
        const type = rawType.toLowerCase() as StructuredDirectiveType;
        const lines = match.split(/\r?\n/);
        const firstLine = lines[0] ?? '';
        const inlinePart = firstLine.replace(/^##\s*(earnings|transcript)\s*/i, '').trim();
        const blockLines = lines.slice(1).map((line) => line.trim()).filter(Boolean);
        const rawSegments = [inlinePart, ...blockLines].filter(Boolean);
        const raw = rawSegments.join("\n");
        const params = parseKeyValuePairs(raw);
        directives.push({ type, params, raw });
        return "";
    });

    // Normalize extra whitespace created by removals
    cleanedText = cleanedText
        .split(/\r?\n/)
        .filter((line, idx, arr) => {
            if (line.trim() !== "") return true;
            return idx === 0 || arr[idx - 1].trim() !== "";
        })
        .join("\n")
        .trim();

    return { cleanedText, directives };
};